// Explorer über die Ablage eines Kundenakten-Ordners (Plan 01.10.2026, B2):
// die Dateien des alten Shares, wie sie im Objektspeicher liegen — mit
// Vorschau, OHNE Zuweisung. So lässt sich nachsehen, wem eine Akte gehört,
// bevor man sie zuweist.
//
// Datenquelle sind die Ebenen-Endpunkte (`kundenaktenAblageService`), nicht
// ein kompletter Index im Client: ein Ordner kann 65.000 Dateien haben, je
// Klick kommt nur die Ebene. Der Baum links kennt alle Ordnerpfade (eine
// Abfrage), die Dateien kommen je Ebene.
//
// Die Vorschau ist dieselbe wie im Reiter Dokumente: PDF, Bild und Text über
// `useFileViewer` (Blob), .msg/.eml über den Mail-Viewer mit Ablage-Quelle,
// Office über ONLYOFFICE in der Leseansicht. Alles andere lädt herunter.

import * as React from "react";
import { FileExplorer } from "../documents/explorer/FileExplorer";
import { normalizeFolderPath, type ExplorerFile, type ExplorerFolder } from "../documents/explorer/explorerModel";
import { useFileViewer, useOfficeEditingEnabled } from "../documents/useDocumentViewer";
import { canPreviewInline, isMailFileLike, isOfficeLike } from "../documents/documentKind";
import { MailFileViewer } from "../documents/MailFileViewer";
import { baueMailDateiAntworten } from "../documents/mailDateiAntworten";
import { useMailContextOptional } from "../../context/MailContext";
import { OfficeDocumentView } from "../documents/OfficeDocumentView";
import { useWindowManager } from "../windowManager/WindowManagerContext";
import { kundenaktenAblageService, type AblageDatei, type AblageEbene } from "../../services/kundenaktenAblageService";
import { Toast } from "../redesign/Toast";
import { useAkteAblage, type AkteAblageZiel } from "./akteAblage";

/** Ohne Virtualisierung: pro Ebene in Blöcken rendern. */
const CHUNK = 200;

/** Eine Datei der Ablage öffnen — Vorschau wie im Reiter Dokumente. */
export function useAblageVorschau(): (d: AblageDatei) => void {
  const { openWindow } = useWindowManager();
  const dateiViewer = useFileViewer();
  const officeVerfuegbar = useOfficeEditingEnabled();
  // Eigene Adressen erst beim Klick lesen — das Mailfenster lebt länger als der
  // Stand der Postfachliste beim Öffnen.
  const mailCtx = useMailContextOptional();
  const mailCtxRef = React.useRef(mailCtx);
  mailCtxRef.current = mailCtx;
  return React.useCallback(
    (d: AblageDatei) => {
      if (isMailFileLike(d.fileName, d.mimeType)) {
        // T-219 (28.10.2026): auf eine gespeicherte Mail direkt antworten, wie im
        // Reiter Dokumente (T-247). Die Ablage hat keine Dokument-Id und keinen
        // Kontakt — darum ohne Weiterleiten und ohne Vorgangs-/Kontaktbezug.
        const aktionen = baueMailDateiAntworten({
          eigeneAdressen: () => [
            ...(mailCtxRef.current?.mailboxes ?? []).map((m) => m.mailboxAddress),
            ...(mailCtxRef.current?.currentMailbox ? [mailCtxRef.current.currentMailbox] : []),
          ],
          // Dynamisch: das Verfassen-Fenster zieht den halben Mail-Bereich nach sich.
          oeffne: (params) =>
            void import("../mail/ComposeMailWindow").then((m) => m.openComposeWindow(openWindow, params)),
        });
        openWindow({
          id: `ablage-mail-${d.id}`,
          title: d.fileName,
          initialMaximized: true,
          render: () => (
            <MailFileViewer
              quelle={{
                key: `ablage-${d.id}`,
                fetchParsed: () => kundenaktenAblageService.mailParsed(d.id),
                fetchAttachmentBlob: (index) => kundenaktenAblageService.mailAttachmentBlob(d.id, index),
              }}
              aktionen={aktionen}
            />
          ),
        });
        return;
      }
      if (officeVerfuegbar && isOfficeLike(d.fileName, d.mimeType)) {
        openWindow({
          id: `ablage-office-${d.id}`,
          title: d.fileName,
          initialMaximized: true,
          render: () => (
            <OfficeDocumentView
              fileName={d.fileName}
              kennung={`ablage-${d.id}`}
              configLaden={() => kundenaktenAblageService.officeConfig(d.id)}
            />
          ),
        });
        return;
      }
      if (canPreviewInline(d.fileName, d.mimeType)) {
        dateiViewer({
          key: `ablage-${d.id}`,
          fileName: d.fileName,
          mimeType: d.mimeType,
          load: () => kundenaktenAblageService.blob(d.id),
          onDownload: () => void kundenaktenAblageService.herunterladen(d.id, d.fileName),
        });
        return;
      }
      void kundenaktenAblageService.herunterladen(d.id, d.fileName);
    },
    [openWindow, dateiViewer, officeVerfuegbar],
  );
}

/** Pfad der Oberfläche (`"a/b/"`) → Pfad der API (`"a/b"`). */
const apiPfad = (p: string): string => p.replace(/\/+$/, "");

/** Kinder eines Pfads aus der flachen Liste aller Ordnerpfade. */
export const kinderAus = (alle: string[], pfad: string): ExplorerFolder[] => {
  const base = apiPfad(pfad);
  const praefix = base ? `${base}/` : "";
  return alle
    .filter((o) => o.startsWith(praefix) && o.length > praefix.length && !o.slice(praefix.length).includes("/"))
    .map((o) => ({ kind: "folder" as const, name: o.slice(praefix.length), path: `${o}/` }));
};

export const AblageExplorer: React.FC<{
  folderId: number;
  /** Beschriftung der Wurzel — der Ordnername am Share. */
  rootLabel: string;
  /**
   * Wohin Gezogenes (Dateien, Outlook-Mails, Postfach-Mails/-Anhänge) geht.
   * Ohne Angabe nimmt die Liste nichts an.
   */
  ablageZiel?: AkteAblageZiel;
}> = ({ folderId, rootLabel, ablageZiel }) => {
  const [path, setPath] = React.useState("");
  const [ebenen, setEbenen] = React.useState<Record<string, AblageEbene>>({});
  const [baum, setBaum] = React.useState<string[] | null>(null);
  const [expandedPaths, setExpandedPaths] = React.useState<ReadonlySet<string>>(() => new Set<string>());
  const [laedt, setLaedt] = React.useState(true);
  const [fehler, setFehler] = React.useState<string | null>(null);
  const [shown, setShown] = React.useState(CHUNK);
  const vorschau = useAblageVorschau();
  const uid = React.useId().replace(/:/g, "");
  const dnd = useAkteAblage(ablageZiel ?? { grund: "" }, path);

  const p = apiPfad(path);
  const ebene = ebenen[p];

  React.useEffect(() => {
    let lebt = true;
    if (ebenen[p]) {
      setLaedt(false);
      return;
    }
    setLaedt(true);
    kundenaktenAblageService
      .ebene(folderId, p)
      .then((e) => lebt && setEbenen((alt) => ({ ...alt, [p]: e })))
      .catch(() => lebt && setFehler("Der Ordner konnte nicht geladen werden."))
      .finally(() => lebt && setLaedt(false));
    return () => {
      lebt = false;
    };
    // `ebenen` nur lesen; der Cache ändert die Identität bei jeder Ebene.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderId, p]);

  React.useEffect(() => {
    let lebt = true;
    kundenaktenAblageService
      .baum(folderId)
      .then((b) => lebt && setBaum(b.ordner))
      .catch(() => lebt && setBaum([]));
    return () => {
      lebt = false;
    };
  }, [folderId]);

  React.useEffect(() => {
    setShown(CHUNK);
  }, [path]);

  const folders: ExplorerFolder[] = React.useMemo(
    () => (ebene?.ordner ?? []).map((o) => ({ kind: "folder" as const, name: o.name, path: `${o.pfad}/`, count: o.dateien })),
    [ebene],
  );
  const files: ExplorerFile<AblageDatei>[] = React.useMemo(
    () =>
      (ebene?.dateien ?? []).map((d) => ({
        kind: "file" as const,
        key: String(d.id),
        name: d.fileName,
        dir: p,
        size: d.sizeBytes,
        date: d.modifiedAt,
        mimeType: d.mimeType,
        tag: d.isSchaden ? "Schaden" : null,
        payload: d,
      })),
    [ebene, p],
  );

  const treeChildren = React.useCallback((pfad: string) => (baum ? kinderAus(baum, pfad) : undefined), [baum]);

  const navigate = (next: string) => {
    const n = normalizeFolderPath(next);
    setPath(n);
    if (n) {
      setExpandedPaths((prev) => {
        const s = new Set(prev);
        let acc = "";
        for (const seg of n.split("/").filter(Boolean)) {
          acc += seg + "/";
          s.add(acc);
        }
        return s;
      });
    }
  };

  const onToggleExpand = (pfad: string) => {
    setExpandedPaths((prev) => {
      const s = new Set(prev);
      if (s.has(pfad)) s.delete(pfad);
      else s.add(pfad);
      return s;
    });
  };

  return (
    <>
      <FileExplorer<AblageDatei>
        rootLabel={rootLabel}
        path={path}
        onNavigate={navigate}
        folders={folders}
        files={files.slice(0, shown)}
        browsing
        treeChildren={treeChildren}
        expandedPaths={expandedPaths}
        onToggleExpand={onToggleExpand}
        loading={laedt}
        hasMore={shown < files.length}
        onLoadMore={() => setShown((n) => n + CHUNK)}
        scrollContainerId={`ablageScroll-${uid}`}
        emptyText={laedt ? "Lade Ordner …" : "Keine Dateien in diesem Ordner."}
        onOpenFile={(f) => f.payload && vorschau(f.payload)}
        onDownload={(f) => f.payload && void kundenaktenAblageService.herunterladen(f.payload.id, f.name)}
        {...(ablageZiel ? dnd.props : {})}
      />
      {ablageZiel ? dnd.anzeige : null}
      <Toast open={!!fehler} message={fehler ?? ""} tone="error" onClose={() => setFehler(null)} />
    </>
  );
};
